import { useState } from "react";

export const faqs = [
  {
    q: "How fast can I get a passport in Sarasota, FL?",
    a: "Through Fast Passport Center, expedited processing can be as fast as 24 hours depending on your eligibility and the processing option you select. Standard government processing through a post office or acceptance clerk in Sarasota can take 2–6 months, especially during busy travel seasons. For arts travelers, Ringling College students, and seasonal residents with upcoming gallery tours or study abroad programs, that timeline is simply not viable.",
  },
  {
    q: "Can Ringling College students expedite a passport for study abroad?",
    a: "Absolutely. Ringling College of Art and Design students heading to study abroad programs in Florence, Paris, Tokyo, or other international destinations are a significant portion of our Sarasota client base. Many students (and their parents) discover passport issues during the pre-departure scramble — expired passports, name mismatches, or first-time applications. We can process expedited applications on compressed timelines that align with academic program deadlines, which are non-negotiable.",
  },
  {
    q: "What if my passport expires before an international arts tour or festival?",
    a: "This happens frequently in a culturally active community like Sarasota. Whether you are attending the Edinburgh Festival Fringe, a gallery week in Paris, or a donor trip to the Vienna Opera, many cultural events have fixed dates that cannot be moved. We strongly recommend checking your passport expiration at least three months before any international arts travel. If you are within two weeks of departure, expedited processing is your most reliable option.",
  },
  {
    q: "Can I renew my passport fast before a Europe trip from Sarasota?",
    a: "Yes. Whether you are flying out of Sarasota-Bradenton International (SRQ) or connecting through Tampa International (TPA), we can process your renewal in as little as 2–9 business days depending on the service level you choose. Many Sarasota residents book European gallery tours, performing arts festivals, or design research trips on relatively short notice — our expedited service is designed for exactly that scenario.",
  },
  {
    q: "How quickly can travelers near Bradenton or Venice receive an expedited passport?",
    a: "Bradenton, Venice, Lakewood Ranch, and Englewood travelers have the same access to our expedited services as Sarasota residents. Since we operate remotely, your physical location in Manatee or Sarasota County does not affect processing speed. Ship your documents overnight from anywhere in the region, and we handle the rest. Most clients in these communities receive their passports within 2–9 business days.",
  },
  {
    q: "Can retired arts patrons expedite a passport renewal in Sarasota?",
    a: "Yes — retired and senior arts patrons are a significant portion of our Sarasota client base. Whether you are planning a gallery tour of the Veneto, a performing arts festival in Salzburg, or a cultural exchange in Montreal, we make the renewal process straightforward. You do not need to drive to a passport agency or wait in long lines. Start online, ship your documents, and we handle the expedited submission.",
  },
  {
    q: "Do I need to visit an office to use Fast Passport Center?",
    a: "No — most Sarasota travelers start online and ship their documents via overnight courier. There is no local Fast Passport Center office in Sarasota, but that is the point: you do not need one. Start your order online, follow the checklist, and ship your documents overnight from anywhere in the Sarasota or Bradenton area.",
  },
  {
    q: "What documents do I need for an expedited passport?",
    a: "Most applicants need: proof of U.S. citizenship (current or expired U.S. passport, birth certificate, or naturalization certificate), a government-issued photo ID, completed passport forms, and passport photos. We provide a personalized checklist after you start your order, so you know exactly what is required for your specific situation.",
  },
  {
    q: "Is Fast Passport Center an official government agency?",
    a: "No — Fast Passport Center is a private, approved and registered U.S. Department of State passport courier service. We are not a government agency. We work within the official passport system to provide expedited processing through reserved courier submission availability.",
  },
  {
    q: "Can I get a same-day passport in Sarasota, FL?",
    a: "Same-day passport processing requires an in-person appointment at a regional passport agency — and the nearest one to Sarasota is Miami, about 220 miles away via I-75. That agency only serves life-or-death emergencies with strict eligibility, and walk-ins are not accepted. If you don't qualify or can't get an appointment, Fast Passport Center can process your passport in as little as 2–3 business days — the fastest option available to most Sarasota travelers. Start your order and we'll confirm your timeline based on your travel date.",
  },
  {
    q: "Can I get a passport in 3 days in Sarasota?",
    a: "Yes — 2 to 3 business days is our fastest processing tier and the quickest option available to most Sarasota-area travelers outside of a regional agency emergency appointment. Ship your documents overnight from anywhere in the Sarasota or Bradenton area and we submit through our reserved courier access. No Miami drive, no appointment required.",
  },
  {
    q: "Can I get a passport in one week in Sarasota?",
    a: "A one-week turnaround is one of our most common requests from Sarasota travelers. Whether you are preparing for a European arts tour, a Caribbean cruise from Tampa, or a Ringling College study abroad departure, 5–7 business day processing is available for most eligible applicants. Ship your documents overnight and we handle the rest.",
  },
  {
    q: "The Miami Passport Agency has no appointments — what are my options?",
    a: "Miami Passport Agency appointments are routinely unavailable 2–4 weeks out, and the 220-mile drive from Sarasota makes it impractical for most travelers even when appointments exist. Fast Passport Center's registered courier channel bypasses the agency entirely — no appointment, no drive to Miami. We submit through reserved access daily and most Sarasota travelers receive their passport within their selected timeframe.",
  },
  {
    q: "Can I get an urgent passport renewal before a Europe trip from Sarasota?",
    a: "Yes. Whether you are flying from SRQ or connecting through Tampa International, we can process your passport renewal in as little as 2–9 business days. Many Sarasota arts travelers and seasonal residents book European tours on relatively short notice — our expedited courier service is built for exactly that scenario. Start your order and we'll confirm availability for your departure date.",
  },
];

export default function SarasotaFAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section id="faq" className="bg-white py-20 md:py-28">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Questions?</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Frequently Asked <span className="text-[#F6C344]">Questions</span>
          </h2>
          <p className="text-gray-500 text-base">
            Everything Sarasota and Cultural Coast travelers ask us most often about expedited passport services.
          </p>
        </div>

        <div className="space-y-2">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 overflow-hidden"
            >
              <button
                className="w-full flex items-center justify-between gap-4 p-5 text-left cursor-pointer"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
              >
                <span className="font-semibold text-[#1A365D] text-sm leading-snug">{faq.q}</span>
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#1A365D] flex-shrink-0">
                  <i
                    className={`text-white text-sm transition-transform duration-200 ${
                      openIndex === i ? "ri-subtract-line" : "ri-add-line"
                    }`}
                  ></i>
                </div>
              </button>
              {openIndex === i && (
                <div className="px-5 pb-5">
                  <p className="text-gray-500 text-sm leading-relaxed border-t border-gray-200 pt-4">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <p className="text-gray-500 text-sm mb-4">Still have questions? Our team is here to help.</p>
          <a
            href="tel:8772530084"
            className="inline-flex items-center gap-2 bg-[#1A365D] hover:bg-[#0F2240] text-white font-semibold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap"
          >
            <i className="ri-phone-line"></i> Call 877-253-0084
          </a>
        </div>
      </div>
    </section>
  );
}